import { ACMEResponse } from '@b2b/types/core';
import angular, { IHttpService } from 'angular';
import { BehaviorSubject, map } from 'rxjs';
import { ThemesHelperService } from '../themesHelper/types';
import { ActiveApp, STORED_VALUE_ACTIVATE_SLUG, STORED_VALUE_AUTHORIZE_SLUG } from './types';

export interface AppsState {
  status: 'idle' | 'loading' | 'error' | 'loaded';
  data: ActiveApp[] | null;
}

/**
 * Loads the tenant's active apps (`GET /v2/b2b/apps/activeapps`) and exposes
 * helpers to check which extendables are active. Used to gate features such
 * as gift card redemption behind the stored value app's extendable slug
 * being present for the tenant.
 */
export class AppsService {
  static $inject = ['$http', 'ThemesHelperService'];

  private stateSubject = new BehaviorSubject<AppsState>({ status: 'idle', data: null });
  /** Tenant the current state was loaded for; a different current tenant triggers a reload. */
  private tenantId: string | null = null;
  state$ = this.stateSubject.asObservable();
  activeApps$ = this.stateSubject.pipe(map((model) => (model.status === 'loaded' ? model.data ?? [] : [])));

  constructor(
    private $http: IHttpService,
    private themesHelperService: ThemesHelperService,
  ) {}

  /**
   * True when an active app of the current tenant extends the given slug,
   * ignoring any `@version` suffix. Apps loaded for a previous tenant never
   * match. Does not trigger a load; call `ensureLoaded()` first.
   */
  hasExtendable(slug: string): boolean {
    if (this.tenantId !== this.currentTenantId()) {
      return false;
    }

    return (this.stateSubject.value.data ?? []).some((app) =>
      (app.extendables ?? []).some(
        (extendable) => typeof extendable?.slug === 'string' && extendable.slug.split('@')[0] === slug,
      ),
    );
  }

  /** True when the tenant can redeem stored value (gift cards), i.e. the stored_value_authorize extendable is active. */
  canRedeemStoredValue(): boolean {
    return this.hasExtendable(STORED_VALUE_AUTHORIZE_SLUG);
  }

  /**
   * True when the tenant can activate/configure stored value (gift card)
   * tiles, i.e. the stored_value_activate extendable is active. Gates the
   * backoffice "Gift Cards" config menu item (DEV-13600) and its settings
   * page (DEV-13071) — distinct from `canRedeemStoredValue()`, which gates
   * checkout redemption.
   */
  canActivateStoredValue(): boolean {
    return this.hasExtendable(STORED_VALUE_ACTIVATE_SLUG);
  }

  /**
   * Loads active apps once per tenant; calls while loading/loaded for the
   * current tenant are no-ops. A tenant switch (CSR/deputy sign-in, logout and
   * login) happens without a page reload, so a different tenant reloads.
   */
  ensureLoaded(): void {
    const { status } = this.stateSubject.value;
    if ((status === 'loading' || status === 'loaded') && this.tenantId === this.currentTenantId()) {
      return;
    }
    this.loadActiveApps();
  }

  loadActiveApps(): void {
    const tenantId = this.currentTenantId();
    this.tenantId = tenantId;

    if (!tenantId) {
      this.stateSubject.next({ status: 'error', data: null });
      return;
    }

    this.stateSubject.next({ status: 'loading', data: null });

    this.$http
      .get<ACMEResponse<ActiveApp[]>>('/v2/api/b2b/apps/activeapps', {
        headers: {
          'x-tenant-id': tenantId,
        },
      })
      .then((response) => {
        if (this.tenantId === tenantId) {
          this.stateSubject.next({ status: 'loaded', data: response.data.payload ?? [] });
        }
      })
      .catch(() => {
        if (this.tenantId === tenantId) {
          this.stateSubject.next({ status: 'error', data: null });
        }
      });
  }

  private currentTenantId(): string | null {
    const tenantId = this.themesHelperService.getCompany('tenantId');
    return tenantId ? String(tenantId) : null;
  }
}

export const AppsServiceModule = angular
  .module('acme.common.service.apps', [])
  .service('AppsService', AppsService).name;
